import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, Lock, CheckCircle, Loader, AlertCircle } from 'lucide-react';
import catlogService from '../../services/catlogService';

const PaymentModal = ({ isOpen, onClose }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxPollAttempts = 60;

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsProcessing(false);
      setErrorMessage('');
      setPollAttempts(0);
    }
  }, [isOpen]);

  const pollPaymentStatus = async (reqId) => {
    try {
      const response = await catlogService.getStudyPaymentStatus(reqId);
      
      if (response.data.status === 'completed' && response.data.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
        return true;
      } else if (response.data.status === 'failed') {
        setIsProcessing(false);
        setErrorMessage('Something went wrong');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Poll error:', error);
      return false;
    }
  };

  const startPolling = async (reqId) => {
    setPollAttempts(0);

    const poll = async () => {
      if (pollAttempts >= maxPollAttempts) {
        setIsProcessing(false);
        setErrorMessage('Something went wrong');
        return;
      }

      const completed = await pollPaymentStatus(reqId);
      
      if (!completed) {
        setPollAttempts(prev => prev + 1);
        setTimeout(poll, 5000);
      }
    };

    poll();
  };

  const handlePayNow = async () => {
    setIsProcessing(true);
    setErrorMessage('');

    try {
      const createResponse = await catlogService.createStudyPaymentRequest();
      const reqId = createResponse.data.requestId;
      
      await catlogService.automateStudyPayment(reqId);
      startPolling(reqId);
    } catch (error) {
      setIsProcessing(false);
      setErrorMessage('Something went wrong');
    }
  };

  const paymentAmount = parseFloat(process.env.REACT_APP_STUDY_ABROAD_PAYMENT_AMOUNT) || 149;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="absolute top-4 right-4 text-white hover:text-gray-200 disabled:opacity-50 z-10"
          >
            <X size={24} />
          </button>

          {/* Content - Same design as OverviewSection payment card */}
          <div className="bg-gradient-to-r from-[#202356] to-[#2542D4] rounded-lg p-8 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Lock size={20} />
              <h3 className="text-xl font-semibold">Complete Your Application Journey</h3>
            </div>
            <p className="text-sm text-blue-100 mb-4">
              Unlock document uploads and personalized SOP assistance
            </p>
            <p className="text-xs text-white text-opacity-90 mb-4">
              We'll manage your complete application from start to finish, beginning the moment you unlock access. Share your documents and story with us, and we'll craft a compelling Statement of Purpose that highlights your unique strengths and aspirations. Through our direct relationships with academic institutions, we understand what each admissions committee is looking for and will position your application accordingly.
            </p>
            <ul className="space-y-2 text-xs text-white text-opacity-90 mb-4">
              <li className="flex items-center gap-2">
                <CheckCircle size={14} />
                <span>Upload documents to start your application</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle size={14} />
                <span>We'll create your expert SOP</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle size={14} />
                <span>Benefit from our institutional insights</span>
              </li>
            </ul>

            {/* Error Message */}
            {errorMessage && (
              <div className="mb-4 p-3 bg-red-500 bg-opacity-20 border border-red-300 rounded-lg flex items-start gap-2">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <p className="text-sm text-white">{errorMessage}</p>
              </div>
            )}

            <button
              onClick={handlePayNow}
              disabled={isProcessing}
              className="w-full bg-white text-[#202356] font-semibold px-6 py-3 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <CreditCard size={20} />
                  <span>Pay ${paymentAmount} Now</span>
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
      )}
    </AnimatePresence>
  );
};

export default PaymentModal;
